import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { TFunction } from 'i18next';
import { useQuery } from '@apollo/client';
import { makeStyles, Theme } from '@material-ui/core/styles';
import GET_RESOURCE_TYPES from 'graph/resource-type/resource-types-query';
import PageContainer from '../../@layout/PageContainer';
import Tile from '../../@common/Tile';
import LikedIcon from '../../../assets/resources_liked.png';
import { FAVORITES } from '../util';
import HeaderBar from '../HeaderBar';
import { ResourceType } from 'types/schema-generated-types';
import { ModuleProps } from 'types';

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      display: 'grid',
      gridGap: 24,
      gridTemplateColumns: `repeat(auto-fit, minmax(327px, 1fr))`,
      gridAutoFlow: 'dense',
      padding: `${theme.spacing(3)}px ${theme.spacing(5)}px`
    }
  };
});

type FavoritesCard = Pick<ResourceType, 'title' | 'description' | 'type'> & {
  id: string;
  imageAsset: { url: string };
};
const getFavoritesCard = (t: TFunction): FavoritesCard => ({
  id: FAVORITES,
  type: FAVORITES,
  imageAsset: { url: LikedIcon },
  title: t('Liked Resources'),
  description: t('Keep all liked resources in one place.')
});

const Resources: React.FC<ModuleProps> = ({ id, ...props }) => {
  const history = useHistory();
  const match = useRouteMatch();
  const classes = useStyles();
  const { t } = useTranslation(id);
  const { loading, error, data: { resourceTypes = [] as ResourceType[] } = {} } = useQuery(
    GET_RESOURCE_TYPES
  );

  const handleTileClick = (id: number | string) => (): void => {
    history.push(`${match.path}/${id}`);
  };
  const favoritesCard = useCallback(() => getFavoritesCard(t), [t]);

  const getResourceTypes = useCallback(
    () => (resourceTypes.length ? [...resourceTypes, favoritesCard()] : []),
    [resourceTypes, favoritesCard]
  );

  return (
    <PageContainer loading={loading} error={error}>
      <HeaderBar id={id} {...props} />
      <div className={classes.container}>
        {resourceTypes.length
          ? getResourceTypes().map((x, idx) => (
              <Tile
                key={`resource_type_tile_${idx}`}
                handleClick={handleTileClick(x.id)}
                icon={x.imageAsset.url}
                title={x.title}
                description={x.description}
              />
            ))
          : null}
      </div>
    </PageContainer>
  );
};

export default Resources;
